import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShoppingBag, Loader2 } from 'lucide-react';

// Public buy-page — does NOT use the AuthContext or send cookies.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public`,
  headers: { 'Content-Type': 'application/json' },
});

const fmt = (n, ccy='usd') => `${(ccy||'usd').toUpperCase()} ${Number(n).toFixed(2)}`;

const PublicBuyPage = ({ slug }) => {
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [selectedBumps, setSelectedBumps] = useState({}); // { bumpId: true|false }

  useEffect(() => {
    publicApi.get(`/commerce/products/${slug}`).then((r) => {
      setProduct(r.data);
      // Initialize selectedBumps based on default_checked flag
      const init = {};
      (r.data.bump_offers || []).forEach((b) => { if (b.default_checked) init[b.id] = true; });
      setSelectedBumps(init);
    })
      .catch((e) => setErr(e?.response?.data?.detail || 'Product not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const buy = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const bumpIds = Object.keys(selectedBumps).filter((k) => selectedBumps[k]);
      const { data } = await publicApi.post('/commerce/checkout', {
        slug, origin_url: window.location.href.split('?')[0],
        customer_email: email || undefined, quantity: 1,
        bump_offer_ids: bumpIds,
      });
      window.location.href = data.url;
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Checkout failed'); setBusy(false); }
  };

  // Live total = product price + sum of selected bump prices
  const bumpsTotal = product
    ? (product.bump_offers || [])
        .filter((b) => selectedBumps[b.id])
        .reduce((s, b) => s + Number(b.price || 0), 0)
    : 0;
  const grandTotal = product ? Number(product.price) + bumpsTotal : 0;

  if (loading) return <div className="fp-shell"><div className="fp-card"><Loader2 className="spin" size={20} /> Loading…</div></div>;
  if (err && !product) return <div className="fp-shell"><div className="fp-card" data-testid="public-buy-error">{err}</div></div>;

  if (!product) return null;

  return (
    <div className="fp-shell">
      <form className="fp-card" onSubmit={buy} data-testid="public-buy-page">
        {product.image_url && <img src={product.image_url} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8, marginBottom: 14 }} />}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 10, background: 'rgba(255,146,0,0.1)', color: '#ff9200', fontSize: 10, fontWeight: 600, marginBottom: 10 }}>
          <ShoppingBag size={10} /> {product.interval === 'one_time' ? 'ONE-TIME PURCHASE' : `RECURRING / ${product.interval.toUpperCase()}`}
        </div>
        <div className="fp-title">{product.name}</div>
        {product.description && <div className="fp-desc" style={{ whiteSpace: 'pre-wrap' }}>{product.description}</div>}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, margin: '14px 0' }}>
          <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>{fmt(product.price, product.currency)}</span>
          {product.interval !== 'one_time' && <span style={{ fontSize: 12, color: 'var(--text-faded)' }}>/ {product.interval}</span>}
        </div>
        <div className="fp-field">
          <label>Email <span className="req">*</span></label>
          <input type="email" className="fp-input" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="buy-email" />
        </div>

        {/* Order-bump add-ons */}
        {product.bump_offers?.length > 0 && (
          <div className="fp-bumps" data-testid="buy-bumps">
            <div className="fp-bumps-hd">Add to your order</div>
            {product.bump_offers.map((b) => (
              <label key={b.id} className={`fp-bump${selectedBumps[b.id] ? ' selected' : ''}`} data-testid={`buy-bump-${b.id}`}>
                <input
                  type="checkbox"
                  checked={!!selectedBumps[b.id]}
                  onChange={() => setSelectedBumps((s) => ({ ...s, [b.id]: !s[b.id] }))}
                  data-testid={`buy-bump-checkbox-${b.id}`}
                />
                <div className="fp-bump-body">
                  <div className="fp-bump-title">{b.label}</div>
                  {b.description && <div className="fp-bump-desc">{b.description}</div>}
                </div>
                <div className="fp-bump-price">+ {fmt(b.price, b.currency)}</div>
              </label>
            ))}
          </div>
        )}

        {/* Live total breakdown */}
        {bumpsTotal > 0 && (
          <div className="fp-breakdown" data-testid="buy-breakdown">
            <div><span>{product.name}</span><b>{fmt(product.price, product.currency)}</b></div>
            {product.bump_offers.filter((b) => selectedBumps[b.id]).map((b) => (
              <div key={b.id}><span>+ {b.label}</span><b>{fmt(b.price, b.currency)}</b></div>
            ))}
            <div className="fp-breakdown-total"><span>Total</span><b>{fmt(grandTotal, product.currency)}</b></div>
          </div>
        )}

        {err && <div className="f-error">{err}</div>}
        <button type="submit" className="fp-submit" disabled={busy || !email} data-testid="buy-submit">
          {busy ? 'Redirecting to checkout…' : `Continue to checkout — ${fmt(grandTotal, product.currency)}`}
        </button>
        <div className="fp-foot">Powered by LegacyCRM · Secure checkout by Stripe</div>
      </form>
    </div>
  );
};

export default PublicBuyPage;
